import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@clerk/react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(", ");
  return e?.message || "Something went wrong";
};

export const stepUpToken = {
  get: () => sessionStorage.getItem("ir_stepup") || "",
  set: (t) => sessionStorage.setItem("ir_stepup", t),
};
export const branchPick = {
  get: () => localStorage.getItem("ir_branch") || "",
  set: (b) => { if (b) localStorage.setItem("ir_branch", b); else localStorage.removeItem("ir_branch"); },
};

export const publicApi = async (method, path, data) => {
  try {
    return (await axios({ method, url: `${API}${path}`, data })).data;
  } catch (e) {
    const err = new Error(errMsg(e));
    err.status = e?.response?.status;
    throw err;
  }
};

export function useApi() {
  const { getToken } = useAuth();
  return useCallback(
    async (method, path, data, opts = {}) => {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      if (stepUpToken.get()) headers["X-Step-Up"] = stepUpToken.get();
      if (branchPick.get()) headers["X-Branch"] = branchPick.get();
      try {
        const r = await axios({ method, url: `${API}${path}`, data, headers, ...opts });
        return r.data;
      } catch (e) {
        if (e?.response?.status === 428) window.dispatchEvent(new Event("stepup-required"));
        const err = new Error(errMsg(e));
        err.status = e?.response?.status;
        throw err;
      }
    },
    [getToken]
  );
}

export function useLoad(path) {
  const api = useApi();
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const reload = useCallback(async () => {
    if (!path) return;
    setState((s) => ({ ...s, loading: true }));
    try {
      setState({ data: await api("get", path), loading: false, error: null });
    } catch (e) {
      setState({ data: null, loading: false, error: e.message });
    }
  }, [api, path]);
  useEffect(() => { reload(); }, [reload]);
  return { ...state, reload };
}

export function usePublic(path) {
  const [data, setData] = useState(null);
  useEffect(() => { if (path) publicApi("get", path).then(setData).catch(() => setData(null)); }, [path]);
  return data;
}

export const download = async (api, path, filename) => {
  const blob = await api("get", path, null, { responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

export const toCsv = (rows) => {
  if (!rows?.length) return "";
  const keys = [...new Set(rows.flatMap((r) => Object.keys(r)))];
  const esc = (v) => { const s = v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  return [keys.join(","), ...rows.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\n");
};

export const saveCsv = (rows, name) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([toCsv(rows)], { type: "text/csv" }));
  a.download = name;
  a.click();
};
